import * as React from 'react'
import { Link } from 'gatsby'
import { toInternalHref } from './internalLink'

// Shared by gatsby-browser.js and gatsby-ssr.js so both render identically.
//
// PrismicLink (and hyperlinks inside PrismicRichText) hands every link to one
// of these two components. Links to our own domain, and bare "#section" links,
// are rendered as in-page navigation instead of a full reload. Jumps to a
// heading on the current page scroll smoothly.
//
// No router hooks here: Gatsby renders slices outside the router at build
// time, so anything location-based has to happen at click time.

const stripSlash = (pathname) => (pathname || '/').replace(/\/+$/, '') || '/'

const splitHash = (to) => {
  const i = to.indexOf('#')
  return i === -1 ? { path: to, hash: '' } : { path: to.slice(0, i), hash: to.slice(i) }
}

// Smooth-scroll to the element a hash points at. Returns false when the
// element is missing so the normal navigation can happen instead.
const scrollToHash = (hash) => {
  if (typeof document === 'undefined') return false
  const id = decodeURIComponent(hash.slice(1))
  const el = id && document.getElementById(id)
  if (!el) return false
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
  window.history.pushState(null, '', hash)
  return true
}

const isPlainClick = (e) => e.button === 0 && !e.metaKey && !e.altKey && !e.ctrlKey && !e.shiftKey && !e.defaultPrevented

const SiteLink = ({ to, children, activeClassName, target, rel, onClick, ...rest }) => {
  const { path, hash } = splitHash(to)

  const handleClick = (e) => {
    if (onClick) onClick(e)
    if (!hash || !isPlainClick(e)) return
    const samePage = path === '' || stripSlash(path) === stripSlash(window.location.pathname)
    if (samePage && scrollToHash(hash)) e.preventDefault()
  }

  if (path === '') {
    // Bare "#section": a plain anchor, enhanced with smooth scrolling.
    return (
      <a data-location='anchor' href={hash} onClick={handleClick} {...rest}>
        {children}
      </a>
    )
  }

  // Gatsby Link checks e.defaultPrevented after calling onClick, so a
  // same-page hash click scrolls instead of navigating.
  return (
    <Link data-location='internal' to={to} activeClassName={activeClassName} onClick={handleClick} {...rest}>
      {children}
    </Link>
  )
}

export const InternalLink = ({ href, children, ...props }) => {
  if (!href) return null
  return (
    <SiteLink to={href} {...props}>
      {children}
    </SiteLink>
  )
}

export const ExternalLink = ({ href, children, ...props }) => {
  if (!href) return null
  const internal = toInternalHref(href)
  if (internal) {
    return (
      <SiteLink to={internal} {...props}>
        {children}
      </SiteLink>
    )
  }
  const { activeClassName, target, rel, ...rest } = props
  return (
    <a data-location='external' href={href} target={target || '_blank'} rel={rel || 'noopener noreferrer'} {...rest}>
      {children}
    </a>
  )
}
